// Domain Store - Analytics and preferences only
// Routing state managed by URL/DomainProvider - this store is for analytics and preferences

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { log } from '@/lib/logger';

// Domain analytics and preferences interface
interface DomainStoreState {
  // Analytics
  domainHistory: Array<{
    domain: string;
    timestamp: Date;
    source: 'navigation' | 'direct' | 'switcher';
  }>;
  
  // User preferences
  lastVisitedRoutes: Record<string, string>; // { billing: '/billing/dashboard', ... }
  
  // Actions
  logDomainVisit: (domain: string, source?: 'navigation' | 'direct' | 'switcher') => void;
  saveLastVisitedRoute: (domain: string, route: string) => void;
  getLastVisitedRoute: (domain: string) => string | undefined;
  clearHistory: () => void;
}

// Create the domain store with persistence
export const useDomainStore = create<DomainStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        domainHistory: [],
        lastVisitedRoutes: {},
        
        // Actions
        logDomainVisit: (domain: string, source = 'direct') => {
          log.debug(`[DOMAIN-STORE] Logging visit to ${domain} (source: ${source})`);
          
          set(state => ({
            domainHistory: [
              ...state.domainHistory.slice(-19), // Keep last 20 entries
              {
                domain,
                timestamp: new Date(),
                source
              }
            ]
          }));
        },
        
        saveLastVisitedRoute: (domain: string, route: string) => {
          log.debug(`[DOMAIN-STORE] Saving last visited route for ${domain}: ${route}`);
          
          set(state => ({
            lastVisitedRoutes: {
              ...state.lastVisitedRoutes,
              [domain]: route
            }
          }));
        },
        
        getLastVisitedRoute: (domain: string) => {
          return get().lastVisitedRoutes[domain];
        },
        
        clearHistory: () => {
          log.debug('[DOMAIN-STORE] Clearing domain history');
          set({ domainHistory: [] });
        }
      }),
      {
        name: 'thrive-ops-domain-store',
        // Persist preferences and history
        partialize: (state) => ({
          lastVisitedRoutes: state.lastVisitedRoutes,
          domainHistory: state.domainHistory.slice(-20) // Keep last 20 in storage
        }),
      }
    ),
    { name: 'domain-store' }
  )
);

